import type { ChatProvider } from '@xsai-ext/shared-providers'

import { generateText } from '@xsai/generate-text'
import { computed, ref } from 'vue'

import { compressDataUrlViaCanvas } from '../stores/chat-helpers'
import { useConsciousnessStore } from '../stores/modules/consciousness'
import { useProvidersStore } from '../stores/providers'

const VISION_PROMPT = '请用2-6个中文字简短描述这个表情包的内容和情绪。只输出描述文字，不要加任何引号、标点或其他符号。例如：开心猫咪、生气、竖大拇指、疑惑'

const MAX_CONCURRENCY = 3
/** vision 识别专用：尺寸和质量都低于聊天默认值，压低 token 用量和延迟 */
const VISION_MAX_DIM = 256
const VISION_QUALITY = 0.7

export function deduplicateName(name: string, existingNames: string[]): string {
  if (!existingNames.includes(name))
    return name

  let i = 2
  while (existingNames.includes(`${name}${i}`))
    i++

  return `${name}${i}`
}

async function createChatConfig(consciousnessStore: ReturnType<typeof useConsciousnessStore>, providersStore: ReturnType<typeof useProvidersStore>): Promise<Record<string, unknown> | null> {
  const providerId = consciousnessStore.activeProvider
  const model = consciousnessStore.activeModel
  if (!providerId || !model)
    return null

  const metadata = providersStore.getProviderMetadata(providerId)
  const credentials = providersStore.getProviderConfig(providerId)
  if (!metadata || !credentials)
    return null

  const provider = await metadata.createProvider(credentials) as ChatProvider
  if (typeof provider.chat !== 'function')
    return null

  return { ...provider.chat(model) }
}

/**
 * vision 识别前的图片预处理：只对 data URL 走 canvas 压到 256×0.7 JPEG，
 * http(s) URL 保持原样直接交给模型（避免再跑一次 canvas 解码）。
 */
async function prepareVisionImageUrl(url: string): Promise<string> {
  if (!url.startsWith('data:'))
    return url
  try {
    return await compressDataUrlViaCanvas(url, VISION_MAX_DIM, { format: 'jpeg', quality: VISION_QUALITY })
  }
  catch {
    return url
  }
}

async function describeImage(imageUrl: string, chatConfig: Record<string, unknown>): Promise<string> {
  const optimizedUrl = await prepareVisionImageUrl(imageUrl)
  const messages = [
    {
      role: 'user',
      content: [
        { type: 'text', text: VISION_PROMPT },
        { type: 'image_url', image_url: { url: optimizedUrl } },
      ],
    },
  ]

  const result = await generateText({
    ...chatConfig,
    messages,
  } as any)

  const text = (result.text || '').trim().replace(/["'「」『』【】\s]/g, '')
  return text.slice(0, 20) || ''
}

async function runWithConcurrency<T>(tasks: (() => Promise<T>)[], limit: number, signal?: AbortSignal): Promise<T[]> {
  const results: T[] = Array.from({ length: tasks.length })
  let index = 0

  async function worker() {
    while (index < tasks.length) {
      if (signal?.aborted)
        break
      const i = index++
      results[i] = await tasks[i]()
    }
  }

  await Promise.all(Array.from({ length: Math.min(limit, tasks.length) }, () => worker()))
  return results
}

export interface AutoNameResult {
  index: number
  name: string
  success: boolean
}

export function useStickerAutoName() {
  const consciousnessStore = useConsciousnessStore()
  const providersStore = useProvidersStore()

  const isNaming = ref(false)
  const namingProgress = ref(0)
  const namingTotal = ref(0)
  const lastError = ref<string | null>(null)
  let abortController: AbortController | null = null

  const isAvailable = computed(() => {
    return !!(consciousnessStore.activeProvider && consciousnessStore.activeModel)
  })

  function cancelNaming() {
    abortController?.abort()
  }

  async function autoName(imageUrl: string, existingNames: string[]): Promise<string | null> {
    lastError.value = null
    const config = await createChatConfig(consciousnessStore, providersStore)
    if (!config)
      return null

    try {
      const raw = await describeImage(imageUrl, config)
      if (!raw)
        return null
      return deduplicateName(raw, existingNames)
    }
    catch (err) {
      const msg = (err as Error).message || String(err)
      lastError.value = `自动命名失败: ${msg.slice(0, 120)}`
      console.warn('表情包自动命名失败:', err)
      return null
    }
  }

  async function batchAutoName(
    images: { url: string, fallbackName: string }[],
    existingNames: string[],
  ): Promise<AutoNameResult[]> {
    lastError.value = null
    const config = await createChatConfig(consciousnessStore, providersStore)
    if (!config) {
      return images.map((img, i) => ({ index: i, name: img.fallbackName, success: false }))
    }

    abortController = new AbortController()
    const signal = abortController.signal

    isNaming.value = true
    namingProgress.value = 0
    namingTotal.value = images.length
    const usedNames = [...existingNames]
    let abortAll = false

    const tasks = images.map((img, i) => async (): Promise<AutoNameResult> => {
      if (abortAll || signal.aborted) {
        namingProgress.value++
        const name = deduplicateName(img.fallbackName, usedNames)
        usedNames.push(name)
        return { index: i, name, success: false }
      }

      try {
        const raw = await describeImage(img.url, config)
        namingProgress.value++
        if (!raw)
          return { index: i, name: deduplicateName(img.fallbackName, usedNames), success: false }

        const name = deduplicateName(raw, usedNames)
        usedNames.push(name)
        return { index: i, name, success: true }
      }
      catch (err) {
        namingProgress.value++
        const msg = (err as Error).message || String(err)
        if (msg.includes('401') || msg.includes('403') || msg.includes('not support') || msg.includes('unsupported')) {
          abortAll = true
          lastError.value = `自动命名失败: ${msg.slice(0, 120)}`
        }
        const name = deduplicateName(img.fallbackName, usedNames)
        usedNames.push(name)
        return { index: i, name, success: false }
      }
    })

    try {
      return await runWithConcurrency(tasks, MAX_CONCURRENCY, signal)
    }
    finally {
      isNaming.value = false
      abortController = null
    }
  }

  return {
    isAvailable,
    autoName,
    batchAutoName,
    cancelNaming,
    isNaming,
    namingProgress,
    namingTotal,
    lastError,
  }
}
